import { useState, useRef, useEffect, useCallback } from "react";
import { buildVoiceSystemPrompt } from "./voice-system-prompt";
import VoiceWaveform from "./voice-waveform";
import { VOICE_MODEL, VOICE_ENDPOINT, planAudioChunk, createPlaybackDiagnostics } from "./voice-playback";
import { ReceiverBackdrop } from "./components/world/ReceiverPrimitives";
import "./receiver-world.css";

const CALL_STATE = {
  CONNECTING: "connecting",
  ACTIVE: "active",
  MUTED: "muted",
  ENDING: "ending",
  GENERATING: "generating",
  ENDED: "ended",
  ERROR: "error",
};

const COMPLETION_PHRASES = [
  "your prompt is ready",
  "you'll see it on the next screen",
  "see it on the next screen",
];

const PROMPT_SIGNAL_RE = /here\s+is\s+your\s+personal\s+prompt\s*:?/i;
const PROMPT_END_RE = /your\s+prompt\s+is\s+ready/i;
const AUDIO_SETTLE_MS = 750;
const COMPLETION_GRACE_MS = 8000;
const HARD_CEILING_GRACE_MS = 120000;
const VOICE_TOOL_KILL_SWITCH_KEY = "tgc_voice_tools_enabled";
const ENABLE_VOICE_FUNCTION_TOOLS = true;
const CALL_MAX_SECONDS = 1200;
const WIND_DOWN_SECONDS_REMAINING = 300;
const CLOSING_SOON_SECONDS_REMAINING = 90;
const MUST_CLOSE_SECONDS_REMAINING = 60;
const PARROTING_RECOVERY_TURNS = 3;
const STICKY_REMINDER_INTERVAL_TURNS = 5;
const ANTI_PARROTING_STICKY_TEXT = "Open with a forward move: a question, original observation, or pivot. Never open by restating, summarizing, labeling, or paraphrasing the user's last turn.";
const PARROTING_RECOVERY_STICKY_TEXT = `${ANTI_PARROTING_STICKY_TEXT} For this recovery response, begin with a question or original observation and do not reference, name, summarize, label, or paraphrase the user's immediately previous topic.`;
const AI_CALIBRATION_STICKY_TEXT = 'On the next response, ask: "Quick one before we get going — have you used AI much before?" Do this before any profile, work, hobby, or comfort-profile thread.';
const HARD_CLOSE_INSTRUCTION = "This is a system-level closing instruction. Stop asking new questions. Move directly into a brief close: reflect one specific thing you heard, offer one small next step only if it naturally fits, say a warm goodbye, and end with exactly: Your prompt is ready — you'll see it on the next screen.";
const PARROTING_CALLOUT_RE = /\b(parrot(?:ing)?|(?:you|you're|you are|stop|don't|dont|do not|quit|keep)\s+(?:just\s+)?(?:repeat(?:ing)?|mirror(?:ing)?|reflect(?:ing)?|summari[sz](?:e|ing))|saying (?:my|the) words back|words back)\b/i;
const AI_CALIBRATION_RE = /\b(?:have you|do you|did you)\s+(?:used?|use)\s+(?:ai|a\.i\.)\s+much\b/i;

function hasParrotingCallout(text) {
  return PARROTING_CALLOUT_RE.test(text);
}

function hasAiCalibrationQuestion(text) {
  return AI_CALIBRATION_RE.test(text);
}

const VOICE_FUNCTION_TOOLS = [
  {
    type: "function",
    name: "check_conversation_state",
    description: "Check the current phase and basic state of this voice conversation, including response pacing guidance.",
    parameters: {
      type: "object",
      properties: {
        current_user_message: {
          type: "string",
          description: "Optional latest user message or short context for why state is being checked.",
        },
      },
    },
  },
  {
    type: "function",
    name: "update_conversation_memory",
    description: "Store a short note about something the user said that may matter later in this same call, and receive a compact memory packet for current-call context.",
    parameters: {
      type: "object",
      properties: {
        note: {
          type: "string",
          description: "A concise note, 200 characters maximum.",
        },
        category: {
          type: "string",
          enum: ["user_stated_fact", "emotional_signal", "topic_introduced"],
        },
      },
      required: ["note", "category"],
    },
  },
  {
    type: "function",
    name: "check_session_timing",
    description: "Check how much call time remains and whether to continue or begin winding down.",
    parameters: {
      type: "object",
      properties: {},
    },
  },
];

function getVoiceFunctionToolsEnabled() {
  if (!ENABLE_VOICE_FUNCTION_TOOLS) return false;
  if (typeof window === "undefined") return true;

  const params = new URLSearchParams(window.location.search);
  const urlValue = params.get("voiceTools");
  if (urlValue === "off" || urlValue === "false" || urlValue === "0") return false;
  if (urlValue === "on" || urlValue === "true" || urlValue === "1") return true;

  try {
    return window.localStorage.getItem(VOICE_TOOL_KILL_SWITCH_KEY) !== "false";
  } catch (_) {
    return true;
  }
}

function extractPromptFromTranscript(transcript) {
  if (!transcript) return "";
  const signalMatch = transcript.match(PROMPT_SIGNAL_RE);
  if (!signalMatch) return transcript.trim();
  const after = transcript.slice(signalMatch.index + signalMatch[0].length);
  const endMatch = after.match(PROMPT_END_RE);
  const body = endMatch ? after.slice(0, endMatch.index) : after;
  return body.trim();
}

function float32ToBase64PCM16(float32Array) {
  const int16 = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const clamped = Math.max(-1, Math.min(1, float32Array[i]));
    int16[i] = Math.round(clamped * 32767);
  }
  const bytes = new Uint8Array(int16.buffer);
  let binary = "";
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

function base64PCM16ToFloat32(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const int16 = new Int16Array(bytes.buffer);
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i++) {
    float32[i] = int16[i] / 32768.0;
  }
  return float32;
}

function formatDuration(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function buildSessionTimingGuidance(secondsRemaining) {
  if (secondsRemaining <= MUST_CLOSE_SECONDS_REMAINING) {
    return {
      recommendation: "must_close_now",
      closing_instruction: HARD_CLOSE_INSTRUCTION,
      style: "2-3 sentences, warm and brief",
      allow_new_threads: false,
      should_end_call: true,
    };
  }

  if (secondsRemaining <= CLOSING_SOON_SECONDS_REMAINING) {
    return {
      recommendation: "close_on_next_response",
      closing_instruction: HARD_CLOSE_INSTRUCTION,
      style: "2-3 sentences, warm and brief",
      allow_new_threads: false,
      should_end_call: true,
    };
  }

  if (secondsRemaining <= WIND_DOWN_SECONDS_REMAINING) {
    return {
      recommendation: "begin_winding_down",
      closing_instruction: "Do not open a large new thread. Consolidate around one specific thing the user shared, then ask at most one grounded follow-up only if it helps the call land.",
      style: "brief, grounded, no big pivots",
      allow_new_threads: false,
      should_end_call: false,
    };
  }

  return {
    recommendation: "continue",
    closing_instruction: "Continue naturally. You may keep exploring, but avoid sprawling branches that will be hard to land later.",
    style: "match the current conversation phase",
    allow_new_threads: true,
    should_end_call: false,
  };
}

export default function VoiceConversation({ profile, token, voice, onComplete, onFallbackToText, previewState = null, previewError = "", previewSpeaking = false }) {
  const [callState, setCallState] = useState(previewState || CALL_STATE.CONNECTING);
  const [errorMessage, setErrorMessage] = useState(previewError);
  const [isSpeaking, setIsSpeaking] = useState(previewSpeaking);
  const [duration, setDuration] = useState(0);
  const [showWrapWarning, setShowWrapWarning] = useState(false);
  const [connectionWarning, setConnectionWarning] = useState("");

  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const nextPlayTimeRef = useRef(0);
  const playbackDiagnosticsRef = useRef(null);
  if (!playbackDiagnosticsRef.current) playbackDiagnosticsRef.current = createPlaybackDiagnostics();
  const transcriptRef = useRef("");
  const turnsRef = useRef([]);
  const sessionStartRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const callStateRef = useRef(CALL_STATE.CONNECTING);
  const isAssistantSpeakingRef = useRef(false);
  const pendingFinalizeTimeoutRef = useRef(null);
  const pendingFinalizeReasonRef = useRef(null);
  const intentionalCloseRef = useRef(false);
  const wakeLockRef = useRef(null);
  const hasInitRef = useRef(false);
  const hasTriggeredWrapRef = useRef(false);
  const hasShownWarningRef = useRef(false);
  const hasFinalizedRef = useRef(false);
  const voiceToolsEnabledRef = useRef(getVoiceFunctionToolsEnabled());
  const memoryNotesRef = useRef([]);
  const toolCallsLogRef = useRef([]);
  const runtimeRemindersLogRef = useRef([]);
  const baseInstructionsRef = useRef("");
  const runtimeReminderActiveRef = useRef(false);
  const hasFlushedToolLogsRef = useRef(false);
  const pendingToolContinuationTimeoutRef = useRef(null);
  const parrotingRecoveryTurnsRef = useRef(0);
  const lastStickyReminderAssistantTurnRef = useRef(0);
  const aiCalibrationAskedRef = useRef(false);
  const awaitingAiCalibrationAnswerRef = useRef(false);
  const aiCalibrationAnsweredRef = useRef(false);

  const updateCallState = useCallback((s) => {
    callStateRef.current = s;
    setCallState(s);
  }, []);

  const requestWakeLock = useCallback(async () => {
    if (!navigator.wakeLock?.request || wakeLockRef.current) return;
    try {
      const wakeLock = await navigator.wakeLock.request("screen");
      wakeLockRef.current = wakeLock;
      wakeLock.addEventListener("release", () => {
        if (wakeLockRef.current === wakeLock) {
          wakeLockRef.current = null;
        }
      });
    } catch (_) {
      // Wake Lock is best-effort. Connection loss handling below is the fallback.
    }
  }, []);

  const flushToolLogs = useCallback((reason) => {
    if (hasFlushedToolLogsRef.current) return;
    hasFlushedToolLogsRef.current = true;

    if (
      !voiceToolsEnabledRef.current &&
      toolCallsLogRef.current.length === 0 &&
      runtimeRemindersLogRef.current.length === 0
    ) return;

    // eslint-disable-next-line no-console
    console.groupCollapsed(`[voice-tools] session ended: ${reason}`);
    // eslint-disable-next-line no-console
    console.log("[voice-tools] enabled", voiceToolsEnabledRef.current);
    // eslint-disable-next-line no-console
    console.log("[voice-tools] memory_notes", memoryNotesRef.current);
    // eslint-disable-next-line no-console
    console.log("[voice-tools] tool_calls", toolCallsLogRef.current);
    // eslint-disable-next-line no-console
    console.log("[voice-tools] runtime_reminders", runtimeRemindersLogRef.current);
    // eslint-disable-next-line no-console
    console.groupEnd();
  }, []);

  const cleanup = useCallback(() => {
    flushToolLogs("cleanup");
    if (pendingToolContinuationTimeoutRef.current) {
      clearTimeout(pendingToolContinuationTimeoutRef.current);
      pendingToolContinuationTimeoutRef.current = null;
    }
    if (pendingFinalizeTimeoutRef.current) {
      clearTimeout(pendingFinalizeTimeoutRef.current);
      pendingFinalizeTimeoutRef.current = null;
      pendingFinalizeReasonRef.current = null;
    }
    if (processorRef.current) {
      try { processorRef.current.disconnect(); } catch (_) {}
      processorRef.current = null;
    }
    if (sourceRef.current) {
      try { sourceRef.current.disconnect(); } catch (_) {}
      sourceRef.current = null;
    }
    if (analyserRef.current) {
      try { analyserRef.current.disconnect(); } catch (_) {}
      analyserRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    if (wsRef.current) {
      intentionalCloseRef.current = true;
      try { wsRef.current.close(1000); } catch (_) {}
      wsRef.current = null;
    }
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    isAssistantSpeakingRef.current = false;
  }, [flushToolLogs]);

  const handleError = useCallback((message) => {
    updateCallState(CALL_STATE.ERROR);
    setErrorMessage(message);
    cleanup();
  }, [cleanup, updateCallState]);

  const finalizeCall = useCallback(async () => {
    if (hasFinalizedRef.current) return;
    hasFinalizedRef.current = true;

    cleanup();
    updateCallState(CALL_STATE.GENERATING);

    const turns = turnsRef.current.slice();
    const fallback = () => extractPromptFromTranscript(transcriptRef.current);

    if (turns.length === 0) {
      onComplete(fallback());
      return;
    }

    try {
      const response = await fetch("/api/voice-prompt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ turns, profile }),
      });

      if (!response.ok) {
        onComplete(fallback());
        return;
      }

      const data = await response.json();
      const prompt = typeof data?.prompt === "string" ? data.prompt.trim() : "";
      onComplete(prompt || fallback());
    } catch (_) {
      onComplete(fallback());
    }
  }, [cleanup, onComplete, profile, updateCallState]);

  const getPlaybackRemainingMs = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx || ctx.state === "closed") return 0;
    return Math.max(0, (nextPlayTimeRef.current - ctx.currentTime) * 1000);
  }, []);

  const cancelCompletionFinalize = useCallback(() => {
    if (
      pendingFinalizeTimeoutRef.current &&
      pendingFinalizeReasonRef.current === "completion"
    ) {
      clearTimeout(pendingFinalizeTimeoutRef.current);
      pendingFinalizeTimeoutRef.current = null;
      pendingFinalizeReasonRef.current = null;
      if (callStateRef.current === CALL_STATE.ENDING) {
        updateCallState(CALL_STATE.ACTIVE);
      }
    }
  }, [updateCallState]);

  const requestFinalize = useCallback((reason, { minDelayMs = COMPLETION_GRACE_MS, maxDelayMs = HARD_CEILING_GRACE_MS } = {}) => {
    if (hasFinalizedRef.current) return;

    if (pendingFinalizeTimeoutRef.current) {
      clearTimeout(pendingFinalizeTimeoutRef.current);
    }

    updateCallState(CALL_STATE.ENDING);
    pendingFinalizeReasonRef.current = reason;

    const playbackDelay = isAssistantSpeakingRef.current
      ? getPlaybackRemainingMs() + AUDIO_SETTLE_MS
      : 0;
    const delay = Math.min(Math.max(minDelayMs, playbackDelay), maxDelayMs);

    pendingFinalizeTimeoutRef.current = setTimeout(() => {
      pendingFinalizeTimeoutRef.current = null;
      pendingFinalizeReasonRef.current = null;
      finalizeCall();
    }, delay);
  }, [finalizeCall, getPlaybackRemainingMs, updateCallState]);

  const getElapsedSeconds = useCallback(() => {
    if (!sessionStartRef.current) return 0;
    return Math.max(0, Math.floor((Date.now() - sessionStartRef.current) / 1000));
  }, []);

  const getConversationPhase = useCallback(() => {
    const elapsedSeconds = getElapsedSeconds();
    const turnCount = turnsRef.current.length;
    const secondsRemaining = Math.max(0, CALL_MAX_SECONDS - elapsedSeconds);

    if (secondsRemaining <= CLOSING_SOON_SECONDS_REMAINING) return "closing";
    if (turnCount <= 3) return "opening";
    if (turnCount <= 8) return "exploring";
    return "deepening";
  }, [getElapsedSeconds]);

  const getAssistantTurnCount = useCallback(() => (
    turnsRef.current.filter((turn) => turn.role === "assistant").length
  ), []);

  const logToolCall = useCallback((name, input, output, error = null) => {
    toolCallsLogRef.current.push({
      name,
      input,
      output,
      error,
      timestamp: new Date().toISOString(),
      turn_count: turnsRef.current.length,
      elapsed_seconds: getElapsedSeconds(),
    });
  }, [getElapsedSeconds]);

  const logRuntimeReminder = useCallback((reason, text) => {
    runtimeRemindersLogRef.current.push({
      reason,
      text,
      timestamp: new Date().toISOString(),
      turn_count: turnsRef.current.length,
      assistant_turn_count: getAssistantTurnCount(),
      elapsed_seconds: getElapsedSeconds(),
    });
  }, [getAssistantTurnCount, getElapsedSeconds]);

  const buildInstructionsWithRuntimeReminder = useCallback((reminderText) => (
    `${baseInstructionsRef.current}

RUNTIME REMINDER — INTERNAL ONLY:
${reminderText}
Do not quote, mention, summarize, or read this runtime reminder aloud.`
  ), []);

  const sendRuntimeInstructionUpdate = useCallback((text, reason) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !baseInstructionsRef.current) return false;

    ws.send(JSON.stringify({
      type: "session.update",
      session: {
        instructions: buildInstructionsWithRuntimeReminder(text),
      },
    }));
    runtimeReminderActiveRef.current = true;
    logRuntimeReminder(reason, text);
    return true;
  }, [buildInstructionsWithRuntimeReminder, logRuntimeReminder]);

  const clearRuntimeInstructionUpdate = useCallback(() => {
    const ws = wsRef.current;
    if (
      !runtimeReminderActiveRef.current ||
      !ws ||
      ws.readyState !== WebSocket.OPEN ||
      !baseInstructionsRef.current
    ) return;

    ws.send(JSON.stringify({
      type: "session.update",
      session: {
        instructions: baseInstructionsRef.current,
      },
    }));
    runtimeReminderActiveRef.current = false;
    logRuntimeReminder("runtime_reminder_clear", "cleared");
  }, [logRuntimeReminder]);

  const injectRuntimeReminderAfterUserItem = useCallback(() => {
    const assistantTurns = getAssistantTurnCount();

    if (!aiCalibrationAskedRef.current && assistantTurns === 1) {
      sendRuntimeInstructionUpdate(AI_CALIBRATION_STICKY_TEXT, "ai_calibration_gate");
      return;
    }

    if (parrotingRecoveryTurnsRef.current > 0) {
      sendRuntimeInstructionUpdate(PARROTING_RECOVERY_STICKY_TEXT, "parroting_recovery");
      return;
    }

    if (
      assistantTurns >= STICKY_REMINDER_INTERVAL_TURNS &&
      assistantTurns - lastStickyReminderAssistantTurnRef.current >= STICKY_REMINDER_INTERVAL_TURNS
    ) {
      if (sendRuntimeInstructionUpdate(ANTI_PARROTING_STICKY_TEXT, "periodic_anti_parroting")) {
        lastStickyReminderAssistantTurnRef.current = assistantTurns;
      }
    }
  }, [getAssistantTurnCount, sendRuntimeInstructionUpdate]);

  const handleConversationItemCreated = useCallback((msg) => {
    const item = msg.item;
    if (item?.type !== "message" || item.role !== "user") return;

    injectRuntimeReminderAfterUserItem();
  }, [injectRuntimeReminderAfterUserItem]);

  const buildMemoryPacket = useCallback(() => {
    const recentNotes = memoryNotesRef.current.slice(-5).map((entry) => ({
      note: entry.note,
      category: entry.category,
    }));

    return {
      active: recentNotes.length > 0,
      recent_notes: recentNotes,
      guidance: recentNotes.length > 0
        ? "This is what you already know about this call. Do not repeat, summarize, or echo these notes back. Let them silently shape your next question or response in your own words."
        : "No current-call memory notes yet.",
    };
  }, []);

  const getResponseGuidance = useCallback((phase) => {
    const withForwardMoveGuidance = (guidance) => {
      const responseGuidance = {
        ...guidance,
        opener: "Open with a forward move: a question, original observation, or pivot. Never open with a restatement, compact topic label, summary, or paraphrase of the user's last turn.",
        anti_parroting: "Do not use memory notes, profile facts, or the user's own wording as the response-opening label.",
        ai_calibration: aiCalibrationAnsweredRef.current
          ? "AI-experience calibration is complete."
          : "Do not begin any profile, work, hobby, or comfort-profile thread until the AI-experience question has been asked and answered.",
      };

      if (parrotingRecoveryTurnsRef.current > 0) {
        responseGuidance.parroting_callout_recovery = {
          active: true,
          responses_remaining: parrotingRecoveryTurnsRef.current,
          required_opener: "Begin with a question or original observation.",
          forbidden_opener: "Do not reference, name, summarize, label, or paraphrase the user's immediately previous topic.",
        };
      }

      return responseGuidance;
    };

    if (phase === "opening") {
      return withForwardMoveGuidance({
        length: "1 sentence, max about 12 words",
        style: "plain, grounded, no setup or summary",
        goal: "make the conversation easy to enter; ask one simple question",
        opening_gate: "Assistant turn 2 must ask whether the user has used AI much before any profile or comfort-profile details, unless the user raised that exact topic first.",
      });
    }

    if (phase === "exploring") {
      return withForwardMoveGuidance({
        length: "2-3 sentences",
        style: "match the user's energy; respond in your own words; ask one question",
        goal: "follow the most alive thread without turning it into an interview",
      });
    }

    if (phase === "closing") {
      return withForwardMoveGuidance({
        length: "2-3 sentences",
        style: "warm and brief; do not open large new threads",
        goal: "help the call land without rushing or reading the keepsake prompt; if session timing says to close, close now",
      });
    }

    return withForwardMoveGuidance({
      length: "3-5 sentences when the topic warrants it",
      style: "include one personal connection, observation, or short story when useful; avoid monologues",
      goal: "make a useful connection or ask the next natural question",
    });
  }, []);

  const runVoiceTool = useCallback((name, args) => {
    const safeArgs = args && typeof args === "object" ? args : {};
    const elapsedSeconds = getElapsedSeconds();
    const secondsRemaining = Math.max(0, CALL_MAX_SECONDS - elapsedSeconds);

    if (name === "check_conversation_state") {
      const phase = getConversationPhase();
      return {
        phase,
        turn_count: turnsRef.current.length,
        elapsed_seconds: elapsedSeconds,
        response_guidance: getResponseGuidance(phase),
      };
    }

    if (name === "update_conversation_memory") {
      const rawNote = typeof safeArgs.note === "string" ? safeArgs.note.trim() : "";
      const category = typeof safeArgs.category === "string" ? safeArgs.category : "topic_introduced";
      const allowedCategories = ["user_stated_fact", "emotional_signal", "topic_introduced"];
      const note = rawNote.slice(0, 200);

      if (note) {
        memoryNotesRef.current.push({
          note,
          category: allowedCategories.includes(category) ? category : "topic_introduced",
          timestamp: new Date().toISOString(),
        });
      }

      return {
        stored: Boolean(note),
        total_notes: memoryNotesRef.current.length,
        memory_packet: buildMemoryPacket(),
      };
    }

    if (name === "check_session_timing") {
      const timingGuidance = buildSessionTimingGuidance(secondsRemaining);

      return {
        elapsed_seconds: elapsedSeconds,
        seconds_remaining: secondsRemaining,
        ...timingGuidance,
      };
    }

    return {
      available: false,
      recommendation: "continue_without_tool",
    };
  }, [buildMemoryPacket, getConversationPhase, getElapsedSeconds, getResponseGuidance]);

  const scheduleToolContinuation = useCallback(() => {
    if (pendingToolContinuationTimeoutRef.current) {
      clearTimeout(pendingToolContinuationTimeoutRef.current);
    }

    const delay = getPlaybackRemainingMs() + AUDIO_SETTLE_MS;
    pendingToolContinuationTimeoutRef.current = setTimeout(() => {
      pendingToolContinuationTimeoutRef.current = null;
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: "response.create" }));
    }, delay);
  }, [getPlaybackRemainingMs]);

  const handleFunctionCall = useCallback((msg) => {
    if (!voiceToolsEnabledRef.current) return;

    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !msg.call_id) return;

    let args = {};
    try {
      args = msg.arguments ? JSON.parse(msg.arguments) : {};
    } catch (_) {
      const output = {
        available: false,
        recommendation: "continue_without_tool",
      };
      logToolCall(msg.name || "unknown", { raw_arguments: msg.arguments }, output, "invalid_arguments");
      ws.send(JSON.stringify({
        type: "conversation.item.create",
        item: {
          type: "function_call_output",
          call_id: msg.call_id,
          output: JSON.stringify(output),
        },
      }));
      scheduleToolContinuation();
      return;
    }

    try {
      const output = runVoiceTool(msg.name, args);
      logToolCall(msg.name, args, output);
      ws.send(JSON.stringify({
        type: "conversation.item.create",
        item: {
          type: "function_call_output",
          call_id: msg.call_id,
          output: JSON.stringify(output),
        },
      }));
      scheduleToolContinuation();
    } catch (err) {
      const output = {
        available: false,
        recommendation: "continue_without_tool",
      };
      logToolCall(msg.name || "unknown", args, output, err?.message || "tool_error");
      try {
        ws.send(JSON.stringify({
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: msg.call_id,
            output: JSON.stringify(output),
          },
        }));
        scheduleToolContinuation();
      } catch (_) {
        // Fail open. Do not interrupt the voice call if tool plumbing fails.
      }
    }
  }, [logToolCall, runVoiceTool, scheduleToolContinuation]);

  const scheduleAudioChunk = useCallback((float32Data, responseId) => {
    const ctx = audioCtxRef.current;
    if (!ctx || ctx.state === "closed") return;

    isAssistantSpeakingRef.current = true;
    setIsSpeaking(true);
    const sampleRate = 24000;
    const buffer = ctx.createBuffer(1, float32Data.length, sampleRate);
    buffer.copyToChannel(float32Data, 0);

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    if (!analyserRef.current) {
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.6;
      analyser.connect(ctx.destination);
      analyserRef.current = analyser;
    }
    source.connect(analyserRef.current);

    const { startTime, underrunMs } = planAudioChunk(
      ctx.currentTime, nextPlayTimeRef.current
    );
    const timing = playbackDiagnosticsRef.current.chunk(
      responseId, performance.now(), buffer.duration * 1000, underrunMs, ctx.state
    );
    if (underrunMs > 0) {
      // Timing only: never include PCM, transcripts, tokens, or instructions.
      // eslint-disable-next-line no-console
      console.warn("[voice-audio] queue restart", timing);
    }
    source.start(startTime);
    nextPlayTimeRef.current = startTime + buffer.duration;

    source.onended = () => {
      if (nextPlayTimeRef.current <= ctx.currentTime + 0.05) {
        isAssistantSpeakingRef.current = false;
        setIsSpeaking(false);
      }
    };
  }, []);

  const handleWSMessage = useCallback((msg) => {
    if (msg.type === "response.created") {
      playbackDiagnosticsRef.current.begin(msg.response?.id, performance.now());
    }
    if (msg.type === "response.done") {
      const ctx = audioCtxRef.current;
      const summary = playbackDiagnosticsRef.current.finish(
        msg.response?.id, performance.now(),
        ctx ? (nextPlayTimeRef.current - ctx.currentTime) * 1000 : 0
      );
      // Generation finished; queued audio can still be playing. Content is excluded.
      // eslint-disable-next-line no-console
      if (summary) console.info("[voice-audio] response summary", summary);
    }
    if (msg.type === "session.created" || msg.type === "session.updated") {
      // Inspect only identity fields; session instructions are private.
      // eslint-disable-next-line no-console
      console.info("[voice-session]", {
        event: msg.type,
        requestedModel: VOICE_MODEL,
        resolvedModel: msg.session?.model ?? "unknown",
        voice: msg.session?.voice ?? "unknown",
      });
    }

    if (msg.type === "conversation.item.created" || msg.type === "conversation.item.added") {
      handleConversationItemCreated(msg);
      return;
    }

    // Audio output (xAI: response.output_audio.delta with field `audio`).
    // Keep response.audio.delta fallback in case the stream carries OpenAI-style events.
    if (msg.type === "response.output_audio.delta" || msg.type === "response.audio.delta") {
      const payload = msg.audio ?? msg.delta;
      if (payload) {
        const float32 = base64PCM16ToFloat32(payload);
        scheduleAudioChunk(float32, msg.response_id);
      }
      return;
    }

    if (msg.type === "response.function_call_arguments.done") {
      handleFunctionCall(msg);
      return;
    }

    // Transcript deltas — cover both xAI and OpenAI-style names.
    if (
      msg.type === "response.output_audio_transcript.delta" ||
      msg.type === "response.audio_transcript.delta" ||
      msg.type === "response.text.delta"
    ) {
      const piece = msg.delta ?? msg.text;
      if (piece) transcriptRef.current += piece;
      return;
    }

    if (
      msg.type === "response.output_audio_transcript.done" ||
      msg.type === "response.audio_transcript.done" ||
      msg.type === "response.text.done"
    ) {
      const full = msg.transcript ?? msg.text;
      if (full) {
        turnsRef.current.push({ role: "assistant", text: full });
        if (hasAiCalibrationQuestion(full)) {
          aiCalibrationAskedRef.current = true;
          awaitingAiCalibrationAnswerRef.current = true;
        }
        if (parrotingRecoveryTurnsRef.current > 0) {
          parrotingRecoveryTurnsRef.current -= 1;
        }
        clearRuntimeInstructionUpdate();
        const lower = full.toLowerCase();
        const isComplete = COMPLETION_PHRASES.some((p) => lower.includes(p));
        if (isComplete) {
          requestFinalize("completion", { minDelayMs: COMPLETION_GRACE_MS, maxDelayMs: 60000 });
        }
      }
      return;
    }

    // User speech transcription (OpenAI Realtime event; xAI is compatible).
    if (msg.type === "conversation.item.input_audio_transcription.completed") {
      const full = typeof msg.transcript === "string" ? msg.transcript.trim() : "";
      if (full) {
        cancelCompletionFinalize();
        if (hasParrotingCallout(full)) {
          parrotingRecoveryTurnsRef.current = PARROTING_RECOVERY_TURNS;
          sendRuntimeInstructionUpdate(PARROTING_RECOVERY_STICKY_TEXT, "parroting_callout");
        }
        if (awaitingAiCalibrationAnswerRef.current) {
          aiCalibrationAnsweredRef.current = true;
          awaitingAiCalibrationAnswerRef.current = false;
        }
        turnsRef.current.push({ role: "user", text: full });
      }
      return;
    }

    if (msg.type === "error") {
      // eslint-disable-next-line no-console
      console.error("[voice] server error", msg);
      if (
        callStateRef.current !== CALL_STATE.ENDED &&
        callStateRef.current !== CALL_STATE.ENDING &&
        callStateRef.current !== CALL_STATE.GENERATING
      ) {
        handleError("Voice service error. Please try the text conversation instead.");
      }
    }
  }, [scheduleAudioChunk, handleConversationItemCreated, handleFunctionCall, requestFinalize, cancelCompletionFinalize, sendRuntimeInstructionUpdate, clearRuntimeInstructionUpdate, handleError]);

  const setupMicCapture = useCallback((ctx, stream, ws) => {
    const source = ctx.createMediaStreamSource(stream);
    sourceRef.current = source;

    const processor = ctx.createScriptProcessor(4096, 1, 1);
    processorRef.current = processor;

    let hasLoggedFirstAppend = false;

    processor.onaudioprocess = (e) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      const inputData = e.inputBuffer.getChannelData(0);
      const base64Audio = float32ToBase64PCM16(inputData);
      if (!hasLoggedFirstAppend) {
        hasLoggedFirstAppend = true;
        // eslint-disable-next-line no-console
        console.log("[voice] => first input_audio_buffer.append (mic streaming)");
      }
      wsRef.current.send(JSON.stringify({
        type: "input_audio_buffer.append",
        audio: base64Audio,
      }));
    };

    source.connect(processor);
    processor.connect(ctx.destination);
  }, []);

  const startCall = useCallback(async () => {
    try {
      intentionalCloseRef.current = false;
      memoryNotesRef.current = [];
      toolCallsLogRef.current = [];
      runtimeRemindersLogRef.current = [];
      baseInstructionsRef.current = "";
      runtimeReminderActiveRef.current = false;
      parrotingRecoveryTurnsRef.current = 0;
      lastStickyReminderAssistantTurnRef.current = 0;
      aiCalibrationAskedRef.current = false;
      awaitingAiCalibrationAnswerRef.current = false;
      aiCalibrationAnsweredRef.current = false;
      hasTriggeredWrapRef.current = false;
      hasShownWarningRef.current = false;
      hasFlushedToolLogsRef.current = false;
      setShowWrapWarning(false);
      voiceToolsEnabledRef.current = getVoiceFunctionToolsEnabled();
      setConnectionWarning("");
      await requestWakeLock();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      streamRef.current = stream;

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx({ sampleRate: 24000 });
      audioCtxRef.current = ctx;
      nextPlayTimeRef.current = 0;
      playbackDiagnosticsRef.current.reset();

      // xAI realtime WebSocket — token auth via sec-websocket-protocol subprotocol
      const ws = new WebSocket(
        VOICE_ENDPOINT,
        [`xai-client-secret.${token}`]
      );
      wsRef.current = ws;

      ws.onopen = () => {
        setConnectionWarning("");
        voiceToolsEnabledRef.current = getVoiceFunctionToolsEnabled();
        // eslint-disable-next-line no-console
        console.log("[voice] WebSocket open → sending session.update");
        // eslint-disable-next-line no-console
        console.log("[voice-tools] enabled", voiceToolsEnabledRef.current);
        const baseInstructions = buildVoiceSystemPrompt(profile, voice || "eve", {
          enableFunctionTools: voiceToolsEnabledRef.current,
        });
        baseInstructionsRef.current = baseInstructions;

        const sessionUpdate = {
          type: "session.update",
          session: {
            voice: voice || "eve",
            instructions: baseInstructions,
            turn_detection: {
              type: "server_vad",
              threshold: 0.5,
              prefix_padding_ms: 300,
              silence_duration_ms: 600,
            },
            audio: {
              input: {
                format: { type: "audio/pcm", rate: 24000 },
                transcription: { model: "whisper-1" },
              },
              output: { format: { type: "audio/pcm", rate: 24000 } },
            },
          },
        };
        if (voiceToolsEnabledRef.current) {
          sessionUpdate.session.tools = VOICE_FUNCTION_TOOLS;
        }
        ws.send(JSON.stringify(sessionUpdate));

        // Prompt the AI to deliver its opening greeting. xAI does NOT auto-greet —
        // the client must explicitly send response.create to trigger the first utterance.
        // eslint-disable-next-line no-console
        console.log("[voice] sending response.create to trigger opening greeting");
        ws.send(JSON.stringify({ type: "response.create" }));

        setupMicCapture(ctx, stream, ws);

        updateCallState(CALL_STATE.ACTIVE);
        sessionStartRef.current = Date.now();
        durationIntervalRef.current = setInterval(() => {
          const elapsed = Math.floor((Date.now() - sessionStartRef.current) / 1000);
          setDuration(elapsed);

          // 17-min soft warning — show non-interrupting UI hint that we're wrapping up
          if (elapsed >= 1020 && !hasShownWarningRef.current) {
            hasShownWarningRef.current = true;
            setShowWrapWarning(true);
          }

          // 19-min hard signal — instruct Grok to close gracefully without
          // reading the written keepsake. Claude generates that after the call.
          if (
            elapsed >= 1140 &&
            !hasTriggeredWrapRef.current &&
            callStateRef.current === CALL_STATE.ACTIVE &&
            wsRef.current &&
            wsRef.current.readyState === WebSocket.OPEN
          ) {
            hasTriggeredWrapRef.current = true;
            wsRef.current.send(JSON.stringify({
              type: "response.create",
              response: {
                instructions: `${HARD_CLOSE_INSTRUCTION} Do NOT read, dictate, summarize, or introduce the written personal AI prompt. Do NOT say 'Here is your personal prompt.' The written prompt will be generated by the app after this call.`,
              },
            }));
          }

          // 20-min hard ceiling — last resort. Allow queued assistant audio
          // to settle before moving to keepsake generation.
          if (elapsed >= 1200 && callStateRef.current === CALL_STATE.ACTIVE) {
            requestFinalize("hard-ceiling", { minDelayMs: 10000, maxDelayMs: HARD_CEILING_GRACE_MS });
          }
        }, 1000);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          handleWSMessage(msg);
        } catch (_) {}
      };

      ws.onerror = (e) => {
        // eslint-disable-next-line no-console
        console.error("[voice] WebSocket error", e);
        if (callStateRef.current !== CALL_STATE.ENDED && callStateRef.current !== CALL_STATE.ENDING) {
          handleError("Connection failed. Please try the text conversation instead.");
        }
      };

      ws.onclose = (event) => {
        // eslint-disable-next-line no-console
        console.log("[voice] WebSocket close", { code: event.code, reason: event.reason });
        if (intentionalCloseRef.current) return;
        if (
          callStateRef.current !== CALL_STATE.ENDED &&
          callStateRef.current !== CALL_STATE.ENDING &&
          callStateRef.current !== CALL_STATE.GENERATING
        ) {
          setConnectionWarning("Connection lost. If your screen locked, the voice call may have disconnected.");
          handleError("Connection lost. If your screen locked, please restart the voice call or switch to text.");
        }
      };
    } catch (err) {
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        handleError("Microphone access was denied. Please allow microphone access in your browser settings and try again.");
      } else if (err.name === "NotFoundError") {
        handleError("No microphone found. Please connect a microphone and try again.");
      } else {
        handleError("Unable to start voice call. Please try the text conversation instead.");
      }
    }
  }, [token, voice, profile, handleWSMessage, setupMicCapture, handleError, updateCallState, requestWakeLock, requestFinalize]);

  useEffect(() => {
    if (previewState) return undefined;
    if (hasInitRef.current) return;
    hasInitRef.current = true;
    startCall();
    return () => cleanup();
  }, [startCall, cleanup, previewState]);

  // Keep the call alive through mobile visibility changes. Wake Lock is best-effort,
  // so resume audio and surface a clear error if the socket died while hidden.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
          audioCtxRef.current.resume();
        }
        if (
          callStateRef.current === CALL_STATE.ACTIVE ||
          callStateRef.current === CALL_STATE.MUTED
        ) {
          requestWakeLock();
          if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
            setConnectionWarning("Connection lost while the screen was locked. Restart the call or switch to text.");
          }
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [requestWakeLock]);

  const handleMute = () => {
    if (!streamRef.current) return;
    const track = streamRef.current.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    updateCallState(track.enabled ? CALL_STATE.ACTIVE : CALL_STATE.MUTED);
  };

  const handleEndCall = () => {
    if (previewState) {
      onComplete?.("");
      return;
    }
    requestFinalize("manual", { minDelayMs: 500, maxDelayMs: 500 });
  };

  const isMuted = callState === CALL_STATE.MUTED;
  const isConnecting = callState === CALL_STATE.CONNECTING;
  const isEnding = callState === CALL_STATE.ENDING;
  const isGenerating = callState === CALL_STATE.GENERATING;
  const isError = callState === CALL_STATE.ERROR;
  const isActive = callState === CALL_STATE.ACTIVE || callState === CALL_STATE.MUTED;

  return (
    <main className="tgc-receiver-shell tgc-voice-shell" aria-label="Voice conversation" style={{
      position: "relative",
      height: "100dvh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      background: "#1a1714",
      color: "#e8e0d4",
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      overflow: "hidden",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap');
        @keyframes bgFadeIn { from { opacity:0; } to { opacity:1; } }
        @keyframes titleGlow {
          0%,100% { filter: drop-shadow(0 0 6px rgba(232,183,107,0.35)); }
          50%      { filter: drop-shadow(0 0 14px rgba(232,183,107,0.6)); }
        }
        @keyframes pulse {
          0%,100% { opacity:1; transform:scale(1); }
          50%      { opacity:0.7; transform:scale(0.95); }
        }
        @keyframes fadeIn {
          from { opacity:0; transform:translateY(10px); }
          to   { opacity:1; transform:translateY(0); }
        }
        .vc-bg { background-image: url('/backgrounds/recipient-landing-mobile.jpg'); animation: bgFadeIn 0.8s ease forwards; }
        @media (min-width: 768px) {
          .vc-bg { background-image: url('/backgrounds/recipient-landing-desktop.jpg'); }
        }
      `}</style>

      <ReceiverBackdrop />

      {/* Background image — retained only as a decoded fallback */}
      <div className="vc-bg" style={{
        position: "absolute", inset: 0, zIndex: 0,
        backgroundSize: "cover", backgroundPosition: "center bottom",
      }} />

      {/* Dark overlay for contrast */}
      <div className="tgc-voice-overlay" style={{ position: "absolute", inset: 0, zIndex: 1, background: "rgba(20,10,10,0.55)" }} />

      {/* Header */}
      <div className="tgc-receiver-header tgc-voice-header" style={{
        position: "absolute",
        top: 0, left: 0, right: 0,
        padding: "12px 18px 10px",
        background: "rgba(26,16,14,0.85)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(232,183,107,0.15)",
        zIndex: 3,
      }}>
        <div className="tgc-world-wordmark tgc-receiver-wordmark" style={{
          fontFamily: "'Dancing Script', cursive",
          fontSize: 22,
          fontWeight: 600,
          background: "linear-gradient(180deg,#F5D08A,#C9A02F)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          display: "inline-block",
          animation: "titleGlow 3s 0.8s infinite",
        }}>
          The Gift Conversation
        </div>
        <div className="tgc-receiver-eyebrow" style={{
          fontSize: 10,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: "rgba(232,183,107,0.6)",
          marginTop: 2,
        }}>
          VOICE CALL
        </div>
      </div>

      {/* Main content */}
      <div className="tgc-voice-stage" style={{
        position: "relative",
        zIndex: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 32,
        padding: "0 24px",
        animation: "fadeIn 0.5s ease forwards",
      }}>
        {/* State indicator */}
        {isConnecting && (
          <div className="tgc-voice-state" role="status" aria-live="polite" style={{ textAlign: "center" }}>
            <div className="tgc-voice-state__mark tgc-voice-state__mark--loading" style={{
              width: 80, height: 80, borderRadius: "50%",
              border: "2px solid rgba(232,183,107,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px",
              animation: "pulse 1.5s infinite",
            }}>
              <div style={{
                width: 32, height: 32,
                border: "3px solid transparent",
                borderTopColor: "#c4956a",
                borderRadius: "50%",
                animation: "pulse 0.8s linear infinite",
              }} />
            </div>
            <h1 style={{ fontSize: 17, color: "#e8e0d4", marginBottom: 8 }}>
              Connecting…
            </h1>
            <p style={{ fontSize: 13, color: "#9a8e7e" }}>
              Starting your voice call
            </p>
          </div>
        )}

        {isError && (
          <div className="tgc-voice-state" role="alert" style={{ textAlign: "center", maxWidth: 320 }}>
            <span className="tgc-voice-state__mark" aria-hidden="true" />
            <p style={{
              fontSize: 16, color: "#e8e0d4", lineHeight: 1.7, marginBottom: 24,
            }}>
              {errorMessage}
            </p>
            <button
              className="tgc-voice-primary"
              onClick={onFallbackToText}
              style={{
                background: "linear-gradient(180deg,#F5D08A,#C9A02F)",
                color: "#1a1714", border: "none",
                padding: "13px 36px", borderRadius: 50,
                fontSize: 15, fontWeight: 700, cursor: "pointer",
              }}
            >
              Switch to Text →
            </button>
          </div>
        )}

        {isActive && (
          <>
            {connectionWarning && (
              <div className="tgc-voice-warning" role="alert" style={{
                position: "absolute",
                top: 92,
                left: 18,
                right: 18,
                padding: "10px 14px",
                borderRadius: 12,
                background: "rgba(140,30,30,0.9)",
                border: "1px solid rgba(255,180,150,0.35)",
                color: "#fff0e8",
                fontSize: 13,
                lineHeight: 1.4,
                textAlign: "center",
                zIndex: 5,
              }}>
                {connectionWarning}
              </div>
            )}

            {/* Audio-reactive waveform visualizer */}
            <div className={`tgc-voice-waveform-wrap${isSpeaking ? " tgc-voice-waveform-wrap--speaking" : ""}`}>
              <VoiceWaveform
                analyserRef={analyserRef}
                active={isActive}
                isSpeaking={isSpeaking}
              />
            </div>

            {/* Status text */}
            <div className="tgc-voice-current" role="status" aria-live="polite" style={{ textAlign: "center" }}>
              <p className="tgc-voice-current__primary" style={{
                fontSize: 16, color: "#e8e0d4", marginBottom: 6,
              }}>
                {isSpeaking ? "Listening to your gift…" : "Your turn to speak"}
              </p>
              {showWrapWarning && (
                <p className="tgc-voice-current__secondary" style={{
                  fontSize: 12,
                  color: "rgba(232,183,107,0.75)",
                  marginBottom: 6,
                  animation: "fadeIn 0.6s ease forwards",
                }}>
                  We're wrapping up soon
                </p>
              )}
              <p className="tgc-voice-timer" style={{
                fontSize: 13, color: "rgba(232,183,107,0.5)",
                fontVariantNumeric: "tabular-nums",
              }}>
                {formatDuration(duration)}
              </p>
            </div>

            {/* Controls */}
            <div className="tgc-voice-controls" style={{
              display: "flex",
              gap: 20,
              alignItems: "center",
              marginTop: 8,
            }}>
              <button
                className={`tgc-voice-control${isMuted ? " tgc-voice-control--active" : ""}`}
                onClick={handleMute}
                aria-label={isMuted ? "Unmute microphone" : "Mute microphone"}
                style={{
                  width: 56, height: 56, borderRadius: "50%",
                  background: isMuted
                    ? "rgba(232,183,107,0.15)"
                    : "rgba(255,255,255,0.08)",
                  border: isMuted
                    ? "1px solid rgba(232,183,107,0.5)"
                    : "1px solid rgba(255,255,255,0.15)",
                  cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.2s",
                }}
              >
                {isMuted ? (
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M11 5 6.8 8.5H3.5v7h3.3L11 19V5Z"/><path d="m15 9 6 6M21 9l-6 6"/></svg>
                ) : (
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="9" y="3" width="6" height="12" rx="3"/><path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3M8.5 21h7"/></svg>
                )}
                <span>{isMuted ? "Unmute" : "Mute"}</span>
              </button>

              <button
                className="tgc-voice-control tgc-voice-control--end"
                onClick={handleEndCall}
                aria-label="End call"
                style={{
                  width: 68, height: 68, borderRadius: "50%",
                  background: "rgba(200,60,60,0.85)",
                  border: "none",
                  cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "opacity 0.2s",
                }}
              >
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6.2 15.8a8.5 8.5 0 0 1 11.6 0"/><path d="m4 13 2.8 3.8 2.8-2.1M20 13l-2.8 3.8-2.8-2.1"/></svg>
                <span>End call</span>
              </button>
            </div>

            {isMuted && (
              <p className="tgc-voice-muted-label" style={{
                fontSize: 12,
                color: "rgba(232,183,107,0.6)",
                textAlign: "center",
              }}>
                Microphone muted
              </p>
            )}
          </>
        )}

        {isEnding && (
          <div className="tgc-voice-state" role="status" aria-live="polite" style={{ textAlign: "center" }}>
            <span className="tgc-voice-state__mark tgc-voice-state__mark--loading" aria-hidden="true" />
            <h1 style={{ fontSize: 17, color: "#e8e0d4", marginBottom: 8 }}>
              Wrapping up…
            </h1>
            <p style={{ fontSize: 13, color: "#9a8e7e" }}>
              Preparing your prompt
            </p>
          </div>
        )}

        {isGenerating && (
          <div className="tgc-voice-state" role="status" aria-live="polite" style={{ textAlign: "center" }}>
            <span className="tgc-voice-state__mark tgc-voice-state__mark--loading" aria-hidden="true" />
            <h1 style={{ fontSize: 17, color: "#e8e0d4", marginBottom: 8 }}>
              Crafting your keepsake…
            </h1>
            <p style={{ fontSize: 13, color: "#9a8e7e" }}>
              Writing something for you to keep
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
